import { useCallback, useState, useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util, Loan } from '@finley-technologies/finley-types';

type DateRange = {
  startDate: Util.FullDateStr | null;
  endDate: Util.FullDateStr | null;
};

export type LoansFiltersDrawerState = {
  creditFacilityIds: string[];
  maturityDates: DateRange;
  createdAtDates: DateRange;
  capitalProviderIds: string[];
  calculatedLoanStatuses: Loan.CalculatedLoanStatus[];
};

const DEFAULT_LOAN_STATUSES: Loan.CalculatedLoanStatus[] = [
  ...Loan.CalculatedLoanStatuses,
];

export const useLoansFilters = ({
  creditFacilityIds = [],
  capitalProviderIds = [],
  maturityDates,
  createdAtDates = {
    startDate: null,
    endDate: null,
  },
  calculatedLoanStatuses = DEFAULT_LOAN_STATUSES,
}: Partial<LoansFiltersDrawerState>): {
  filters: LoansFiltersDrawerState;
  setFilters: (inputs: Partial<LoansFiltersDrawerState>) => void;
} => {
  const initialMaturityState = useMemo<DateRange>(() => {
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = commonDate.dateToFullDateString(new Date(), clientTimeZone);
    const threeMonthsAgo = commonDate.addMonths(now, -3);
    return { startDate: threeMonthsAgo, endDate: null };
  }, []);

  // *** Maturity Dates ***
  const [selectedMaturityDates, setSelectedMaturityDates] = useState<DateRange>(
    maturityDates ?? initialMaturityState,
  );

  // *** Created At Dates ***
  const [
    selectedCreatedAtDates,
    setSelectedCreatedAtDates,
  ] = useState<DateRange>(createdAtDates);

  // *** Facilities ***
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>(
    creditFacilityIds,
  );

  // *** Lenders ***
  const [selectedCapitalProviderIds, setSelectedCapitalProviderIds] = useState<
    string[]
  >(capitalProviderIds);

  // *** Loan Statuses ***
  const [selectedLoanStatuses, setSelectedLoanStatuses] = useState<
    Loan.CalculatedLoanStatus[]
  >(calculatedLoanStatuses);

  const setFilters = useCallback(
    (nextFilters: Partial<LoansFiltersDrawerState>): void => {
      if (nextFilters.maturityDates) {
        setSelectedMaturityDates({
          startDate: nextFilters.maturityDates.startDate,
          endDate: nextFilters.maturityDates.endDate,
        });
      }
      if (nextFilters.createdAtDates) {
        setSelectedCreatedAtDates({
          startDate: nextFilters.createdAtDates.startDate,
          endDate: nextFilters.createdAtDates.endDate,
        });
      }
      if (nextFilters.creditFacilityIds) {
        setSelectedFacilityIds(nextFilters.creditFacilityIds);
      }
      if (nextFilters.capitalProviderIds) {
        setSelectedCapitalProviderIds(nextFilters.capitalProviderIds);
      }
      if (nextFilters.calculatedLoanStatuses) {
        setSelectedLoanStatuses(nextFilters.calculatedLoanStatuses);
      }
    },
    [],
  );

  return {
    filters: {
      creditFacilityIds: selectedFacilityIds,
      capitalProviderIds: selectedCapitalProviderIds,
      maturityDates: selectedMaturityDates,
      createdAtDates: selectedCreatedAtDates,
      calculatedLoanStatuses: selectedLoanStatuses,
    },
    setFilters,
  };
};
